import React, { useState } from "react";
import { Form, Input, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getFormattedAmount } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { Tokens } from "Shared/Tokens";
import { useHistory } from "react-router-dom";
import * as assetAction from "store/Actions/asset";
import { mpTapPreview } from "Shared/Mixpanel/AssetsManager";

const PortfolioOnSell = (props) => {
    const { coinInfo = {} } = props;
    const history = useHistory();
    const dispatch = useDispatch();
    const selectedPortfolio = useSelector(
        (state) => state.portfolios.selectedPortfolio
    );
    const asset = useSelector((state) => state.assets.selectedAsset);
    const settings = useSelector((state) => state.settings || {});
    const currSymbol = getCurrencySymbol(settings?.currency || "INR");
    const holdings = asset?.portfolio?.latest?.numberOfCoins || 0;
    const avgBuyPrice = asset?.portfolio?.latest?.marketPrice || 0;
    const currentMarketPrice = coinInfo?.current_price || 0;
    const coinSymbol = String(
        asset?.symbol || coinInfo?.symbol || ""
    ).toUpperCase();

    const [form] = Form.useForm();
    const initialInvestment = Number(asset?.portfolio?.latest?.investment || 0);
    const defaultSellPrice = Number(currentMarketPrice || avgBuyPrice || 0);
    const [formData, setFormData] = useState({
        currentInvestment: initialInvestment,
        averagePPC: Number(avgBuyPrice || 0),
        coins2Sell: Number(holdings || 0),
        marketPrice: defaultSellPrice,
    });
    const isValidSellState = (data) => {
        const qty = Number(data.coins2Sell);
        const price = Number(data.marketPrice);
        const maxHoldings = Number(holdings) || 0;
        return (
            qty > 0 &&
            (maxHoldings <= 0 || qty <= maxHoldings + 1e-8) &&
            price > 0
        );
    };
    const [disabled, setDisabled] = useState(
        !isValidSellState({
            coins2Sell: holdings,
            marketPrice: defaultSellPrice,
        })
    );

    const handleValueChange = (value) => {
        const _formData = {
            ...formData,
            ...value,
        };
        setDisabled(!isValidSellState(_formData));
        setFormData(_formData);
    };

    const handleQuickPercentage = (pct) => {
        const nextCoins = Number(((Number(holdings) || 0) * pct).toFixed(8));
        form.setFieldsValue({ coins2Sell: nextCoins });
        handleValueChange({ coins2Sell: nextCoins });
    };

    const onFinish = (values) => {
        const currentInvestment = Number(
            values.currentInvestment ?? initialInvestment
        );
        const averagePPC = Number(values.averagePPC ?? avgBuyPrice);
        const coins2Sell = Number(values.coins2Sell);
        const marketPrice = Number(values.marketPrice);

        dispatch(
            assetAction.sell({
                currentInvestment,
                averagePPC,
                coins2Sell,
                marketPrice,
            })
        );
        mpTapPreview({ type: "sell" });
        history.push(
            `/portfolios/${selectedPortfolio.name}/assets/${asset.id}/preview/sell`
        );
    };

    const onFinishFailed = (errorInfo) => {
        console.log("Failed:", errorInfo);
    };

    const summaryMetrics = [
        {
            label: "Holdings",
            value: `${getFormattedAmount(holdings)}${
                coinSymbol ? ` ${coinSymbol}` : ""
            }`,
        },
        {
            label: "Avg buy price",
            value: `${currSymbol}${getFormattedAmount(avgBuyPrice)}`,
        },
        {
            label: "Market price",
            value: `${currSymbol}${getFormattedAmount(currentMarketPrice)}`,
        },
    ];

    const exceedsHoldings =
        Number(holdings) > 0 &&
        Number(formData.coins2Sell) > Number(holdings) + 1e-8;
    const estimatedProceeds =
        (Number(formData.coins2Sell) || 0) *
        (Number(formData.marketPrice) || 0);
    const estimatedBasis =
        (Number(formData.coins2Sell) || 0) * (Number(avgBuyPrice) || 0);
    const estimatedPl = estimatedProceeds - estimatedBasis;

    return (
        <div
            style={{
                maxWidth: Tokens.containerMaxWidth,
                margin: "0 auto",
                width: "100%",
            }}
        >
            <div
                style={{
                    backgroundColor: Tokens.card,
                    border: `1px solid ${Tokens.hairline}`,
                    borderRadius: Tokens.radiusCard,
                    padding: "12px 16px",
                    marginBottom: "20px",
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "12px",
                }}
            >
                {summaryMetrics.map((metric) => (
                    <div key={metric.label} style={{ minWidth: 0 }}>
                        <div
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "12px",
                                marginBottom: "4px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {metric.label}
                        </div>
                        <div
                            style={{
                                color: Tokens.textPrimary,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                                fontSize: "13px",
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {metric.value}
                        </div>
                    </div>
                ))}
            </div>
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    preserve: true,
                    currentInvestment: getFormattedAmount(
                        initialInvestment,
                        false
                    ),
                    averagePPC: getFormattedAmount(avgBuyPrice, false),
                    coins2Sell: Number(Number(holdings || 0).toFixed(8)),
                    marketPrice: getFormattedAmount(defaultSellPrice, false),
                }}
                onValuesChange={handleValueChange}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                name="get-portfolio-on-sell"
            >
                <Form.Item
                    label="Current Investment"
                    name="currentInvestment"
                    hidden
                >
                    <Input type="number" />
                </Form.Item>
                <Form.Item label="Average Price" name="averagePPC" hidden>
                    <Input type="number" />
                </Form.Item>
                <Form.Item
                    label={`Holdings to sell${
                        coinSymbol ? ` (${coinSymbol})` : ""
                    }`}
                    name="coins2Sell"
                    validateStatus={exceedsHoldings ? "error" : undefined}
                    help={
                        exceedsHoldings
                            ? `Cannot sell more than your current holdings (${holdings} ${coinSymbol})`
                            : undefined
                    }
                >
                    <Input
                        placeholder=""
                        type="number"
                        value={formData.coins2Sell}
                        step="any"
                        min="0"
                        size="large"
                        style={{
                            fontSize: "16px",
                            fontFamily: Tokens.fontMono,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    />
                </Form.Item>
                {holdings > 0 && (
                    <div
                        style={{
                            display: "flex",
                            gap: "8px",
                            marginBottom: "16px",
                            marginTop: "-8px",
                            flexWrap: "wrap",
                        }}
                    >
                        {[
                            { label: "25%", pct: 0.25 },
                            { label: "50%", pct: 0.5 },
                            { label: "75%", pct: 0.75 },
                            { label: "100% Max", pct: 1 },
                        ].map((item) => (
                            <Button
                                key={item.label}
                                size="small"
                                data-testid={`sell-pct-${item.pct * 100}`}
                                onClick={() => handleQuickPercentage(item.pct)}
                                style={{
                                    backgroundColor: Tokens.raised,
                                    color: Tokens.textSecondary,
                                    border: `1px solid ${Tokens.hairline}`,
                                    borderRadius: Tokens.radiusTag,
                                    fontFamily: Tokens.fontMono,
                                    fontSize: "12px",
                                    height: "32px",
                                    padding: "0 10px",
                                }}
                            >
                                {item.label}
                            </Button>
                        ))}
                    </div>
                )}
                <Form.Item
                    label={`Sell price per coin (${
                        settings?.currency || "USDT"
                    })`}
                    name="marketPrice"
                >
                    <Input
                        placeholder=""
                        type="number"
                        value={formData.marketPrice}
                        step="any"
                        min="0"
                        size="large"
                        style={{
                            fontSize: "16px",
                            fontFamily: Tokens.fontMono,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    />
                </Form.Item>
                {estimatedProceeds > 0 && !exceedsHoldings && (
                    <div
                        data-testid="sell-estimated-proceeds"
                        style={{
                            backgroundColor: Tokens.card,
                            border: `1px solid ${Tokens.hairline}`,
                            borderRadius: Tokens.radiusControl,
                            padding: "10px 14px",
                            marginBottom: "16px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <span
                            style={{
                                color: Tokens.textSecondary,
                                fontSize: "13px",
                            }}
                        >
                            Estimated proceeds
                        </span>
                        <span
                            style={{
                                color: Tokens.textPrimary,
                                fontFamily: Tokens.fontMono,
                                fontVariantNumeric: "tabular-nums",
                                fontWeight: 600,
                                fontSize: "14px",
                            }}
                        >
                            {currSymbol}
                            {getFormattedAmount(
                                estimatedProceeds,
                                true,
                                2
                            )}{" "}
                            <span
                                style={{
                                    color:
                                        estimatedPl >= 0
                                            ? Tokens.positive
                                            : Tokens.negative,
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    marginLeft: "4px",
                                }}
                            >
                                ({estimatedPl >= 0 ? "+" : "-"}
                                {currSymbol}
                                {getFormattedAmount(
                                    Math.abs(estimatedPl),
                                    true,
                                    2
                                )}
                                )
                            </span>
                        </span>
                    </div>
                )}
                <Form.Item>
                    <Button
                        disabled={disabled}
                        type="primary"
                        htmlType="submit"
                        block
                        size="large"
                    >
                        Preview Sell
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default PortfolioOnSell;
